/**
 * Collection Showcase — theaj.js
 * Scroll mode: infinite loop (triple-clone) + momentum drag
 */
(function () {
  'use strict';

  function init() {
    var viewport = document.querySelector('.cs-viewport');
    var track    = document.querySelector('.cs-track');
    if (!viewport || !track) return;

    if (viewport.dataset.mode === 'marquee') {
      initMarquee(viewport, track);
    } else {
      initScroll(viewport, track);
    }
  }

  /* ═══════════════════════════════ MARQUEE ═══════════════════ */
  function initMarquee(viewport, track) {
    var list = track.querySelector('.cs-list');
    if (!list) return;
    var s = 0;
    while (track.scrollWidth < viewport.clientWidth * 2.2 && s++ < 20) {
      track.appendChild(list.cloneNode(true));
    }
    track.style.setProperty('--cs-marquee-dist', '-' + list.scrollWidth + 'px');
  }

  /* ═══════════════════════════════ SCROLL ════════════════════ */
  function initScroll(viewport, track) {
    var list    = track.querySelector('.cs-list');
    var btnPrev = document.querySelector('.cs-nav-btn--prev');
    var btnNext = document.querySelector('.cs-nav-btn--next');
    if (!list) return;

    /* ── Triple-clone for infinite loop ──────────────────────
       Layout: [cloneBefore | original | cloneAfter]
       We start scrollLeft at segW (showing original).
       When scroll drifts into either clone, we silently jump
       by ±segW — the content is identical so it's invisible. */
    var cloneBefore = list.cloneNode(true);
    var cloneAfter  = list.cloneNode(true);
    track.insertBefore(cloneBefore, list);
    track.appendChild(cloneAfter);

    var segW      = 0;
    var dragging  = false;
    var lastX     = 0;
    var lastTime  = 0;
    var velocity  = 0;   // px / ms  (positive = moving left = swipe right)
    var moved     = false;
    var rafId     = null;

    /* Measure after layout, then park at origin of original segment */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        segW = list.getBoundingClientRect().width;
        viewport.scrollLeft = segW;
      });
    });

    /* Silent loop jump — keeps us inside the "real" middle segment */
    function loop() {
      if (!segW) return;
      if (viewport.scrollLeft < segW) {
        viewport.scrollLeft += segW;
      } else if (viewport.scrollLeft >= segW * 2) {
        viewport.scrollLeft -= segW;
      }
    }

    /* ── Native scroll safety net (keyboard / a11y) ─────────── */
    viewport.addEventListener('scroll', function () {
      if (!dragging && !rafId && !animRafId) loop();
    }, { passive: true });

    /* ── Buttons — smooth animated scroll ───────────────────── */
    var animStart     = 0;
    var animTarget    = 0;
    var animStartTime = 0;
    var animRafId     = null;
    var ANIM_DURATION = 420; // ms

    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    function animStep(ts) {
      var p  = Math.min((ts - animStartTime) / ANIM_DURATION, 1);
      viewport.scrollLeft = animStart + (animTarget - animStart) * easeOutCubic(p);

      /* Adjust reference points if a loop jump occurred mid-animation */
      if (viewport.scrollLeft < segW) {
        viewport.scrollLeft += segW;
        animStart  += segW;
        animTarget += segW;
      } else if (viewport.scrollLeft >= segW * 2) {
        viewport.scrollLeft -= segW;
        animStart  -= segW;
        animTarget -= segW;
      }

      animRafId = (p < 1) ? requestAnimationFrame(animStep) : null;
    }

    function animateTo(target) {
      if (animRafId) cancelAnimationFrame(animRafId);
      stopMomentum();
      animStart     = viewport.scrollLeft;
      animTarget    = target;
      animStartTime = performance.now();
      animRafId     = requestAnimationFrame(animStep);
    }

    var scrollAmt = Math.round(viewport.clientWidth * 0.75);

    if (btnPrev) {
      btnPrev.addEventListener('click', function () {
        animateTo(viewport.scrollLeft - scrollAmt);
      });
    }
    if (btnNext) {
      btnNext.addEventListener('click', function () {
        animateTo(viewport.scrollLeft + scrollAmt);
      });
    }

    window.addEventListener('resize', function () {
      scrollAmt = Math.round(viewport.clientWidth * 0.75);
      if (list) segW = list.getBoundingClientRect().width;
    });

    /* ── Momentum ────────────────────────────────────────────── */
    var prevFrameTs  = 0;
    var FRICTION     = 0.94; // per 16.7 ms frame

    function momentumStep(ts) {
      var dt = ts - prevFrameTs;
      if (dt > 64) dt = 16.7; // clamp (tab was hidden)
      prevFrameTs = ts;

      velocity *= Math.pow(FRICTION, dt / 16.7); // frame-rate-independent
      viewport.scrollLeft -= velocity * dt;
      loop();

      if (Math.abs(velocity) > 0.04) {
        rafId = requestAnimationFrame(momentumStep);
      } else {
        rafId = null;
        velocity = 0;
      }
    }

    function stopMomentum() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      velocity = 0;
    }

    /* ── Drag ────────────────────────────────────────────────── */
    function dragStart(x) {
      stopMomentum();
      if (animRafId) { cancelAnimationFrame(animRafId); animRafId = null; }
      dragging = true;
      moved    = false;
      lastX    = x;
      lastTime = performance.now();
      velocity = 0;
      viewport.style.userSelect = 'none';
    }

    function dragMove(x) {
      if (!dragging) return;
      var now = performance.now();
      var dx  = x - lastX;
      var dt  = now - lastTime;
      if (Math.abs(dx) > 1) moved = true;
      if (dt > 0) velocity = dx / dt; // instantaneous velocity
      viewport.scrollLeft -= dx;
      loop();
      lastX    = x;
      lastTime = now;
    }

    function dragEnd() {
      if (!dragging) return;
      dragging = false;
      viewport.style.userSelect = '';
      viewport.style.cursor = 'grab';
      /* Kick off momentum if finger was moving fast enough */
      if (Math.abs(velocity) > 0.08) {
        prevFrameTs = performance.now();
        rafId = requestAnimationFrame(momentumStep);
      }
    }

    /* Mouse */
    viewport.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      e.preventDefault();
      dragStart(e.clientX);
      viewport.style.cursor = 'grabbing';
    });
    document.addEventListener('mousemove', function (e) {
      if (dragging) dragMove(e.clientX);
    });
    document.addEventListener('mouseup', function () {
      if (dragging) dragEnd();
    });

    /* Touch — touch-action: pan-y in CSS gives browser full control of
       vertical scrolling. These listeners only receive horizontal movement,
       so no preventDefault() or direction detection needed at all. */
    viewport.addEventListener('touchstart', function (e) {
      dragStart(e.touches[0].clientX);
    }, { passive: true });

    viewport.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      dragMove(e.touches[0].clientX);
    }, { passive: true });

    viewport.addEventListener('touchend',    dragEnd);
    viewport.addEventListener('touchcancel', dragEnd);

    /* Block card link when the user was dragging, not clicking */
    viewport.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }

  /* ── Boot ──────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
